/**
 * AvailableWorkoutsPanel — trainer-side picker for which workouts a
 * client can self-initiate from the portal.
 *
 * Part of client-initiated workouts (phase 2). The data layer
 * (`client_available_workouts` table) has existed since the original
 * programs migration but was never surfaced — no UI ever wrote to it
 * and the portal never read from it. This panel lets the trainer
 * curate that list.
 *
 * Reads + writes `client_available_workouts` directly. The list
 * matters because:
 *   1. Phase 3 client portal picker reads from this table to populate
 *      "do something else today" options.
 *   2. Without entries, the client can ONLY do whatever's scheduled
 *      today — no extras, no makeups, no flexibility.
 *
 * Default empty. Trainer adds explicit entries via the picker, OR
 * one-clicks "Add all workouts from assigned program" to bootstrap
 * with the program's own template set.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Dumbbell, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/ui/empty-state";
import { notifySuccess, notifyError } from "@/lib/toast";

interface AvailableWorkoutsPanelProps {
  clientId: string;
  clientName: string;
}

interface AvailableWorkoutRow {
  /** client_available_workouts.id — for delete. */
  rowId: string;
  workoutTemplateId: string;
  templateName: string;
  templateDescription: string | null;
}

interface WorkoutTemplateOption {
  id: string;
  name: string;
  description: string | null;
}

export function AvailableWorkoutsPanel({ clientId, clientName }: AvailableWorkoutsPanelProps) {
  const { organization } = useOrganization();
  const [rows, setRows] = useState<AvailableWorkoutRow[]>([]);
  const [templateOptions, setTemplateOptions] = useState<WorkoutTemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [search, setSearch] = useState("");
  const [autoFilling, setAutoFilling] = useState(false);

  const loadAvailable = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("client_available_workouts")
        .select("id, workout_template_id, workout_templates(id, name, description)")
        .eq("client_id", clientId);
      if (error) throw error;
      const mapped: AvailableWorkoutRow[] = (data ?? []).map((r) => {
        // workout_templates is the joined object; supabase types pick the
        // table relation as an object when there's a single FK match.
        const tpl = r.workout_templates as { id: string; name: string; description: string | null } | null;
        return {
          rowId: r.id as string,
          workoutTemplateId: r.workout_template_id as string,
          templateName: tpl?.name ?? "Unknown workout",
          templateDescription: tpl?.description ?? null,
        };
      });
      setRows(mapped);
    } catch (err) {
      console.error("[AvailableWorkoutsPanel] load failed:", err);
      notifyError("Couldn't load available workouts", "Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadTemplateOptions = useCallback(async () => {
    try {
      // Globals + org-scoped, same pattern as the rest of the app.
      let q = supabase
        .from("workout_templates")
        .select("id, name, description")
        .order("name");
      if (organization?.id) {
        q = q.or(`organization_id.is.null,organization_id.eq.${organization.id}`);
      } else {
        q = q.is("organization_id", null);
      }
      const { data, error } = await q;
      if (error) throw error;
      setTemplateOptions((data ?? []) as WorkoutTemplateOption[]);
    } catch (err) {
      console.error("[AvailableWorkoutsPanel] template load failed:", err);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadAvailable();
    loadTemplateOptions();
  }, [loadAvailable, loadTemplateOptions]);

  // Filter pickable templates to those the client doesn't already have.
  const pickableTemplates = useMemo(() => {
    const existingIds = new Set(rows.map((r) => r.workoutTemplateId));
    const filtered = templateOptions.filter((t) => !existingIds.has(t.id));
    if (!search.trim()) return filtered;
    const q = search.toLowerCase();
    return filtered.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q),
    );
  }, [rows, templateOptions, search]);

  const handleAdd = async () => {
    if (!selectedTemplate) return;
    setAdding(true);
    try {
      const { error } = await supabase
        .from("client_available_workouts")
        .insert({ client_id: clientId, workout_template_id: selectedTemplate });
      if (error) throw error;
      setSelectedTemplate("");
      await loadAvailable();
      notifySuccess("Workout added");
    } catch (err) {
      console.error("[AvailableWorkoutsPanel] add failed:", err);
      notifyError(
        "Couldn't add workout",
        err instanceof Error ? err.message : "Try again.",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (rowId: string) => {
    try {
      const { error } = await supabase
        .from("client_available_workouts")
        .delete()
        .eq("id", rowId);
      if (error) throw error;
      await loadAvailable();
      notifySuccess("Workout removed");
    } catch (err) {
      console.error("[AvailableWorkoutsPanel] remove failed:", err);
      notifyError(
        "Couldn't remove workout",
        err instanceof Error ? err.message : "Try again.",
      );
    }
  };

  // One-click: pull every distinct workout_template from this client's
  // assigned programs and bulk-insert them as available. Skips templates
  // that are already in the available list (the unique constraint would
  // reject them anyway).
  const handleAutoFillFromProgram = async () => {
    setAutoFilling(true);
    try {
      // 1. Fetch active program assignments for this client
      const { data: assignments, error: aErr } = await supabase
        .from("client_program_assignments")
        .select("program_id")
        .eq("client_id", clientId)
        .eq("status", "active");
      if (aErr) throw aErr;
      if (!assignments?.length) {
        notifyError("No active programs", "Assign this client to a program first, then try again.");
        setAutoFilling(false);
        return;
      }

      const programIds = assignments.map((a) => a.program_id as string);

      // 2. Fetch distinct workout_template_ids referenced in those programs
      const { data: programDays, error: pErr } = await supabase
        .from("program_days")
        .select("workout_template_id")
        .in("program_id", programIds)
        .not("workout_template_id", "is", null);
      if (pErr) throw pErr;

      const templateIds = Array.from(
        new Set(
          (programDays ?? [])
            .map((d) => d.workout_template_id as string | null)
            .filter((id): id is string => !!id),
        ),
      );

      // 3. Filter out templates already in client_available_workouts
      const existingIds = new Set(rows.map((r) => r.workoutTemplateId));
      const newIds = templateIds.filter((id) => !existingIds.has(id));

      if (newIds.length === 0) {
        notifySuccess("Already up to date — all program workouts are available.");
        setAutoFilling(false);
        return;
      }

      // 4. Bulk insert
      const { error: iErr } = await supabase
        .from("client_available_workouts")
        .insert(
          newIds.map((tid) => ({
            client_id: clientId,
            workout_template_id: tid,
          })),
        );
      if (iErr) throw iErr;

      await loadAvailable();
      notifySuccess(
        `Added ${newIds.length} workout${newIds.length === 1 ? "" : "s"} from the assigned program`,
      );
    } catch (err) {
      console.error("[AvailableWorkoutsPanel] autofill failed:", err);
      notifyError(
        "Couldn't auto-fill",
        err instanceof Error ? err.message : "Try again.",
      );
    } finally {
      setAutoFilling(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Available workouts</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Workouts {clientName.split(" ")[0]} can self-start from the portal —
              for extras, makeups, or "I want to do that one again."
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFillFromProgram}
            disabled={autoFilling}
            className="gap-1.5 shrink-0"
          >
            {autoFilling ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Auto-fill from program
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add row */}
        <div className="flex items-end gap-2">
          <div className="flex-1 min-w-0 space-y-1">
            <Select
              value={selectedTemplate}
              onValueChange={setSelectedTemplate}
              disabled={pickableTemplates.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    pickableTemplates.length === 0
                      ? "All your workouts are already available"
                      : "Pick a workout to add"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {pickableTemplates.length > 6 && (
                  <div className="p-2 border-b border-border">
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search workouts"
                      className="h-8 text-sm"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                {pickableTemplates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    No more workouts to add.
                  </div>
                ) : (
                  pickableTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex flex-col items-start">
                        <span>{t.name}</span>
                        {t.description && (
                          <span className="text-xs text-muted-foreground line-clamp-1">
                            {t.description}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={adding || !selectedTemplate}
            className="gap-1.5"
          >
            {adding ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Add
          </Button>
        </div>

        {/* Existing list */}
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Dumbbell}
            title="No self-start workouts yet"
            description={`${clientName.split(" ")[0]} can only do today's scheduled workout until you add some here. Auto-fill from their program is usually the right starting move.`}
          />
        ) : (
          <ul className="space-y-1.5">
            {rows.map((row) => (
              <li
                key={row.rowId}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/40 transition-colors group"
              >
                <Dumbbell className="h-4 w-4 text-primary shrink-0 opacity-70" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{row.templateName}</p>
                  {row.templateDescription && (
                    <p className="text-xs text-muted-foreground truncate">
                      {row.templateDescription}
                    </p>
                  )}
                </div>
                <Badge variant="outline" size="sm" className="shrink-0">
                  Self-start
                </Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
                  onClick={() => handleRemove(row.rowId)}
                  aria-label={`Remove ${row.templateName}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default AvailableWorkoutsPanel;
